<!-- UserRegistrationFormMobile.vue
     Versão mobile do formulário de cadastro.
     Mesma lógica de UserRegistrationForm.vue, com layout full-screen otimizado para celular.
-->
<script setup>
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from "vue";
import { useRouter } from "vue-router";
import { useUserStore } from "@/stores/useUserStore";
import * as groupService from "@/services/groupService";
import * as userService from "@/services/userService";
import { useOnboardingStore } from "@/stores/useOnboardingStore";
import LegalDocsModal from "@/components/Legal/LegalDocsModal.vue";
import WelcomeMobileModal from "@/components/User/WelcomeMobileModal.vue";

import { trackCompleteRegistration } from "@/utils/tracking";

// --- TOAST ---
const showToast = ref(false);
const toastMessage = ref("");
const toastTimeoutId = ref(null);
const toastType = ref("error");

function showErrorToast(message) {
  toastType.value = "error";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}

function showSuccessToast(message) {
  toastType.value = "success";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}

function resetToastTimeout() {
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
  toastTimeoutId.value = setTimeout(() => { showToast.value = false; }, 3000);
}

// --- LegalDocsModal ---
const showLegalModal = ref(false);
const legalModalDefaultDoc = ref("terms");

function openLegalModal(docId) {
  legalModalDefaultDoc.value = docId;
  showLegalModal.value = true;
}

function closeLegalModal() {
  showLegalModal.value = false;
}

// --- Estado principal ---
const router = useRouter();
const registrationStep = ref(0);
const userStore = useUserStore();
const onboardingStore = useOnboardingStore();

const props = defineProps({
  // "Sou uma escola / instituição" no LoginForm: pede o nome da instituição e
  // o envia no cadastro; o backend grava em users.institution_name e, por
  // ele, atribui o plano "Edu Free" em vez do "Free" individual.
  isSchoolSignup: { type: Boolean, default: false },
});

// login-with / forgot-password: e-mail já tem dono (ver UserRegistrationForm).
const emit = defineEmits(["close", "login-with", "forgot-password"]);
const isLoading = ref(false);
const institutionName = ref("");
const trialPlanGroupId = ref(null);
const showWelcomeMobileModal = ref(false);

const user = ref({
  userName: "",
  fullName: "",
  birthDate: "",
  bio: "",
  country: "",
  profession: "",
  email: "",
  password: "",
  confirmPassword: "",
  agreedToTerms: false,
  phoneE164: "",
  phoneIsWhatsapp: false,
  isWhatsappPublic: false,
  telegramUserName: "",
  instagramUserName: "",
  linkedInUserName: "",
  facebookUserName: "",
});

const groupIds = computed(() =>
  trialPlanGroupId.value ? [trialPlanGroupId.value] : []
);

// Países DDI
const countries = ref([
  { code: "355", name: "Albânia" },
  { code: "49", name: "Alemanha" },
  { code: "376", name: "Andorra" },
  { code: "54", name: "Argentina" },
  { code: "374", name: "Armênia" },
  { code: "43", name: "Áustria" },
  { code: "994", name: "Azerbaijão" },
  { code: "1-242", name: "Bahamas" },
  { code: "1-246", name: "Barbados" },
  { code: "32", name: "Bélgica" },
  { code: "501", name: "Belize" },
  { code: "375", name: "Bielorrússia" },
  { code: "591", name: "Bolívia" },
  { code: "55", name: "Brasil" },
  { code: "359", name: "Bulgária" },
  { code: "357", name: "Chipre" },
  { code: "86", name: "China" },
  { code: "57", name: "Colômbia" },
  { code: "506", name: "Costa Rica" },
  { code: "385", name: "Croácia" },
  { code: "53", name: "Cuba" },
  { code: "45", name: "Dinamarca" },
  { code: "1-767", name: "Dominica" },
  { code: "503", name: "El Salvador" },
  { code: "593", name: "Equador" },
  { code: "421", name: "Eslováquia" },
  { code: "386", name: "Eslovênia" },
  { code: "34", name: "Espanha" },
  { code: "1", name: "Estados Unidos" },
  { code: "372", name: "Estônia" },
  { code: "358", name: "Finlândia" },
  { code: "33", name: "França" },
  { code: "995", name: "Geórgia" },
  { code: "350", name: "Gibraltar" },
  { code: "30", name: "Grécia" },
  { code: "1-473", name: "Granada" },
  { code: "502", name: "Guatemala" },
  { code: "592", name: "Guiana" },
  { code: "509", name: "Haiti" },
  { code: "504", name: "Honduras" },
  { code: "36", name: "Hungria" },
  { code: "353", name: "Irlanda" },
  { code: "354", name: "Islândia" },
  { code: "39", name: "Itália" },
  { code: "1-876", name: "Jamaica" },
  { code: "81", name: "Japão" },
  { code: "371", name: "Letônia" },
  { code: "423", name: "Liechtenstein" },
  { code: "370", name: "Lituânia" },
  { code: "352", name: "Luxemburgo" },
  { code: "356", name: "Malta" },
  { code: "52", name: "México" },
  { code: "373", name: "Moldávia" },
  { code: "377", name: "Mônaco" },
  { code: "382", name: "Montenegro" },
  { code: "505", name: "Nicarágua" },
  { code: "47", name: "Noruega" },
  { code: "31", name: "Países Baixos" },
  { code: "507", name: "Panamá" },
  { code: "595", name: "Paraguai" },
  { code: "51", name: "Peru" },
  { code: "48", name: "Polônia" },
  { code: "1-787", name: "Porto Rico" },
  { code: "351", name: "Portugal" },
  { code: "44", name: "Reino Unido" },
  { code: "420", name: "República Tcheca" },
  { code: "1-809", name: "República Dominicana" },
  { code: "40", name: "Romênia" },
  { code: "7", name: "Rússia" },
  { code: "378", name: "San Marino" },
  { code: "1-869", name: "São Cristóvão e Nevis" },
  { code: "1-758", name: "Santa Lúcia" },
  { code: "1-784", name: "São Vicente e Granadinas" },
  { code: "381", name: "Sérvia" },
  { code: "46", name: "Suécia" },
  { code: "41", name: "Suíça" },
  { code: "597", name: "Suriname" },
  { code: "1-868", name: "Trinidad e Tobago" },
  { code: "90", name: "Turquia" },
  { code: "380", name: "Ucrânia" },
  { code: "598", name: "Uruguai" },
  { code: "379", name: "Vaticano" },
  { code: "58", name: "Venezuela" },
]);

const defaultCountry = countries.value.find((c) => c.code === "55") || countries.value[0];
const phoneParts = ref({ selectedCountry: defaultCountry, ddd: "", number: "" });
const ddiSearchTerm = ref(`${defaultCountry.name} (+${defaultCountry.code})`);
const isAutocompleteVisible = ref(false);
const showPassword = ref(false);
const showConfirmPassword = ref(false);

watch(() => user.value.phoneIsWhatsapp, (isWhatsapp) => {
  if (isWhatsapp === false) user.value.isWhatsappPublic = false;
});

watch(() => user.value.isWhatsappPublic, (isPublic) => {
  if (isPublic === true) user.value.phoneIsWhatsapp = true;
});

const passwordsMismatch = computed(() => {
  const p = user.value.password?.trim();
  const c = user.value.confirmPassword?.trim();
  return p && c && p !== c;
});

const fullNameInputRef = ref(null);
const userNameInputRef = ref(null);
const emailInputRef = ref(null);
const ddiAutocompleteRef = ref(null);
const codeInputRefs = ref([]);

const filteredCountries = computed(() => {
  if (!ddiSearchTerm.value) return countries.value;
  const term = ddiSearchTerm.value.toLowerCase();
  return countries.value.filter(
    (c) => c.name.toLowerCase().includes(term) || c.code.includes(term)
  );
});

const isPhoneValid = computed(() => {
  const selectedCountry = phoneParts.value.selectedCountry;
  return (
    selectedCountry &&
    ddiSearchTerm.value.trim() !== "" &&
    phoneParts.value.ddd.trim().length >= 2 &&
    phoneParts.value.number.replace(/\D/g, "").length >= 8
  );
});

const isAdult = computed(() => {
  if (!user.value.birthDate) return false;
  const birth = new Date(user.value.birthDate);
  const age18 = new Date(birth.getFullYear() + 18, birth.getMonth(), birth.getDate());
  return age18 <= new Date();
});

const maxBirthDate = computed(() => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 18);
  return d.toISOString().split("T")[0];
});

const isStep1Valid = computed(() =>
  (!props.isSchoolSignup || institutionName.value.trim() !== "") &&
  user.value.fullName.trim() !== "" && user.value.birthDate.trim() !== "" && isAdult.value
);

const passwordCriteriaMet = computed(() => {
  const p = user.value.password || "";
  return { length: p.length >= 8, letter: /[a-zA-Z]/.test(p), number: /\d/.test(p) };
});


// ── Conta já existente com este e-mail (mesma regra do formulário desktop) ──
// Consultado ao sair do campo de e-mail. "claimable" = conta de membro de
// círculo sem assinatura própria: o cadastro a reaproveita (nada muda até o
// código ser confirmado; nome e nascimento seguem os gravados) e o passo de
// redes sociais é pulado. "taken" = já tem dono; bloqueia aqui, não no envio.
const emailPrecheck = ref({ email: "", status: null });
const normalizedEmail = () => user.value.email.trim().toLowerCase();
const emailPrecheckApplies = computed(
  () => !!emailPrecheck.value.status && emailPrecheck.value.email === normalizedEmail()
);
const isClaimingAccount = computed(
  () => emailPrecheckApplies.value && emailPrecheck.value.status === "claimable"
);
const isEmailTaken = computed(
  () => emailPrecheckApplies.value && emailPrecheck.value.status === "taken"
);
async function checkRegistrationEmail() {
  const email = normalizedEmail();
  if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
  // Só uma resposta válida é lembrada: falha é reconsultada no próximo blur/"Avançar".
  if (emailPrecheck.value.email === email && emailPrecheck.value.status) return;
  try {
    const response = await userService.precheckRegistrationEmail(email);
    emailPrecheck.value = { email, status: response.data?.status ?? null };
  } catch (e) {
    console.warn("[UserRegistrationFormMobile] Falha no precheck de e-mail:", e.response?.data?.message || e.message);
    emailPrecheck.value = { email, status: null };
  }
}

const isEmailStepValid = computed(
  () => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail())
);

const isStep2Valid = computed(() => {
  const c = passwordCriteriaMet.value;
  // Conta reaproveitada: só senha e termos.
  if (isClaimingAccount.value) {
    return (
      c.length && c.letter && c.number &&
      user.value.password === user.value.confirmPassword &&
      user.value.agreedToTerms
    );
  }
  return (
    user.value.userName.trim() !== "" &&
    user.value.email.trim() !== "" &&
    !isEmailTaken.value &&
    c.length && c.letter && c.number &&
    user.value.password === user.value.confirmPassword &&
    user.value.agreedToTerms &&
    isPhoneValid.value
  );
});

function onDddInput() { phoneParts.value.ddd = phoneParts.value.ddd.replace(/\D/g, ""); }
function onNumberInput() { phoneParts.value.number = phoneParts.value.number.replace(/\D/g, ""); }
function handlePhoneBlur() {
  const clean = phoneParts.value.number.replace(/\D/g, "");
  if (clean.length > 5) {
    phoneParts.value.number = clean.slice(0, clean.length - 4) + "-" + clean.slice(clean.length - 4);
  }
}

const verificationCode = ref(new Array(6).fill(""));

const isCodeComplete = computed(() => verificationCode.value.every((d) => d !== ""));

watch(isCodeComplete, (complete) => {
  if (complete && !isLoading.value) submitVerification();
});

const resendCooldown = ref(0);
const resendTimerId = ref(null);
const isResending = ref(false);

function startResendCooldown() { startResendCooldownWithSeconds(60); }

function startResendCooldownWithSeconds(seconds) {
  if (resendTimerId.value) { clearInterval(resendTimerId.value); resendTimerId.value = null; }
  resendCooldown.value = Math.max(1, Math.ceil(seconds));
  resendTimerId.value = setInterval(() => {
    resendCooldown.value -= 1;
    if (resendCooldown.value <= 0) { clearInterval(resendTimerId.value); resendTimerId.value = null; }
  }, 1000);
}

function formatCooldown(totalSeconds) {
  if (totalSeconds <= 90) return `${totalSeconds}s`;
  return `${Math.ceil(totalSeconds / 60)}min`;
}

async function resendVerificationCode() {
  if (isResending.value || resendCooldown.value > 0) return;
  isResending.value = true;
  try {
    await userStore.resendVerificationCode(user.value.email);
    verificationCode.value.fill("");
    codeInputRefs.value.forEach((input) => { if (input) input.value = ""; });
    if (codeInputRefs.value[0]) codeInputRefs.value[0].focus();
    showSuccessToast("Novo código enviado. O código anterior não é mais válido.");
    startResendCooldown();
  } catch (error) {
    if (error.code === "RATE_LIMIT") {
      startResendCooldownWithSeconds(error.retryAfterSeconds);
      showErrorToast(error.message);
    } else {
      showErrorToast(error.message || "Não foi possível enviar um novo código. Tente novamente.");
    }
  } finally {
    isResending.value = false;
  }
}

function handleCodeInput(event, index) {
  const input = event.target;
  if (!/^\d$/.test(input.value)) { input.value = ""; verificationCode.value[index] = ""; return; }
  verificationCode.value[index] = input.value;
  if (input.value && index < 5) codeInputRefs.value[index + 1].focus();
}

function handleCodeBackspace(event, index) {
  if (verificationCode.value[index]) {
    verificationCode.value[index] = "";
  } else if (!event.target.value && index > 0) {
    codeInputRefs.value[index - 1].focus();
  }
}

async function advanceStep() {
  if (isLoading.value) return;

  // Passo 0: o e-mail decide o caminho (ver UserRegistrationForm.vue).
  if (registrationStep.value === 0) {
    if (!isEmailStepValid.value) { showErrorToast("Informe um e-mail válido."); return; }
    await checkRegistrationEmail();
    if (isEmailTaken.value) return;
    registrationStep.value = isClaimingAccount.value ? 2 : 1;
    return;
  }

  // Conta reaproveitada sai do passo 2 (senha) direto para o envio; no
  // cadastro completo o passo 2 avança para as redes sociais.
  if (registrationStep.value === 2) {
    if (!isStep2Valid.value) { showErrorToast("Preencha os campos obrigatórios."); return; }
    if (!isClaimingAccount.value) { registrationStep.value++; return; }
  }

  if (registrationStep.value === 3 || (registrationStep.value === 2 && isClaimingAccount.value)) {
    if (!isClaimingAccount.value && !isPhoneValid.value) { showErrorToast("O telefone (DDI, DDD e Número) é obrigatório."); return; }

    isLoading.value = true;
    try {
      const { selectedCountry, ddd, number } = phoneParts.value;
      const cleanDDI = selectedCountry.code.replace(/\D/g, "");
      const cleanNumber = number.replace(/\D/g, "");
      const formattedPhone = `+${cleanDDI}${ddd}${cleanNumber}`;

      const payload = {
        // Conta reaproveitada: o backend exige o campo mas o descarta (ver desktop).
        userName: isClaimingAccount.value ? normalizedEmail() : user.value.userName,
        fullName: isClaimingAccount.value ? null : user.value.fullName,
        institutionName: props.isSchoolSignup ? institutionName.value.trim() || null : null,
        birthDate: isClaimingAccount.value ? null : user.value.birthDate,
        country: user.value.country,
        profession: user.value.profession,
        phoneE164: isClaimingAccount.value ? null : formattedPhone,
        email: user.value.email,
        password: user.value.password,
        phoneIsWhatsapp: user.value.phoneIsWhatsapp,
        isWhatsappPublic: user.value.isWhatsappPublic,
        telegramUserName: user.value.telegramUserName,
        instagramUserName: user.value.instagramUserName,
        linkedInUserName: user.value.linkedInUserName,
        facebookUserName: user.value.facebookUserName,
        groupIds: groupIds.value,
        status: 0,
      };

      if (!payload.groupIds.length) throw new Error("Plano de registro não encontrado.");

      await userStore.registerUser(payload);
      registrationStep.value = 4;
    } catch (error) {
      const data = error.response?.data;
      let msg = "Erro ao registrar usuário.";
      if (data?.errors) {
        const messages = Object.values(data.errors).flat();
        if (messages.length > 0) msg = messages.join("\n");
      } else if (data?.message) { msg = data.message; }
      else if (data?.title) { msg = data.title; }
      else if (error.message) { msg = error.message; }
      showErrorToast(msg);
    } finally {
      isLoading.value = false;
    }
  } else if (registrationStep.value < 4) {
    if (registrationStep.value === 1 && !isStep1Valid.value) {
      showErrorToast("Preencha os campos obrigatórios.");
      return;
    }
    registrationStep.value++;
  }
}

// Fechar o cadastro leva à LandingPage (rota raiz), não à tela de login.
function handleCloseRegistration() {
  emit("close");
  router.push({ name: "PublicHome" });
}

function goBackStep() {
  if (registrationStep.value <= 0) {
    emit("close");
    router.push({ name: "PublicHome" });
    return;
  }
  if (registrationStep.value === 2 && isClaimingAccount.value) { registrationStep.value = 0; return; }
  registrationStep.value--;
}

// Indicador: cinco passos no cadastro completo, três na conta reaproveitada.
const stepperItems = computed(() => {
  if (isClaimingAccount.value) {
    return [
      { label: "E-mail", active: registrationStep.value >= 0, done: registrationStep.value > 0 },
      { label: "Senha", active: registrationStep.value >= 2, done: registrationStep.value > 2 },
      { label: "Confirmação de E-mail", active: registrationStep.value === 4, done: false },
    ];
  }
  return [
    { label: "E-mail", active: registrationStep.value >= 0, done: registrationStep.value > 0 },
    { label: "Informações Pessoais", active: registrationStep.value >= 1, done: registrationStep.value > 1 },
    { label: "Conta", active: registrationStep.value >= 2, done: registrationStep.value > 2 },
    { label: "Redes Sociais", active: registrationStep.value >= 3, done: registrationStep.value > 3 },
    { label: "Confirmação de E-mail", active: registrationStep.value === 4, done: false },
  ];
});
const currentStepLabel = computed(() => {
  const items = stepperItems.value;
  const idx = [...items].map((it) => it.active).lastIndexOf(true);
  return items[Math.max(0, idx)]?.label ?? "";
});

async function submitVerification() {
  if (isLoading.value) return;

  const finalCode = verificationCode.value.join("");
  if (finalCode.length !== 6) {
    showErrorToast("Por favor, insira o código de verificação de 6 dígitos.");
    return;
  }

  isLoading.value = true;
  try {
    await userStore.verifyUserEmail({ email: user.value.email, code: finalCode });

    trackCompleteRegistration();

    showSuccessToast("Conta verificada com sucesso!");

    try {
      const loginData = await userStore.login({ email: user.value.email, password: user.value.password, rememberMe: false });
      // Conta reaproveitada: existem "Minha conta" e "Membro de X"; o cadastro é da pessoal.
      if (loginData?.requiresContextSelection) await userStore.confirmLoginContext(null);
    } catch (e) {
      console.error("[UserRegistrationFormMobile] Falha no auto-login pós-verificação:", e);
      isLoading.value = false;
      showErrorToast("Conta verificada, mas não foi possível entrar automaticamente. Faça login para continuar.");
      setTimeout(() => { emit("close"); }, 2200);
      return;
    }

    setTimeout(() => {
      isLoading.value = false;
      showWelcomeMobileModal.value = true;
    }, 800);
  } catch (error) {
    console.error("Erro na verificação:", error);
    showErrorToast(
      error.message || "Não foi possível verificar sua conta. O código pode estar incorreto ou ter expirado."
    );
    verificationCode.value.fill("");
    codeInputRefs.value.forEach((input) => { if (input) input.value = ""; });
    if (codeInputRefs.value[0]) codeInputRefs.value[0].focus();
    isLoading.value = false;
  }
}

function handleWelcomeMobileClose() {
  showWelcomeMobileModal.value = false;
  emit("close");
}

function selectCountry(country) {
  phoneParts.value.selectedCountry = country;
  ddiSearchTerm.value = `${country.name} (+${country.code})`;
  isAutocompleteVisible.value = false;
}
function handleDdiFocus() { ddiSearchTerm.value = ""; isAutocompleteVisible.value = true; }
function handleDdiBlur() {
  setTimeout(() => {
    isAutocompleteVisible.value = false;
    const valid = countries.value.some((c) => `${c.name} (+${c.code})` === ddiSearchTerm.value);
    if (!ddiSearchTerm.value.trim() || !valid) {
      const s = phoneParts.value.selectedCountry;
      ddiSearchTerm.value = `${s.name} (+${s.code})`;
    }
  }, 200);
}
const handleClickOutside = (event) => {
  if (ddiAutocompleteRef.value && !ddiAutocompleteRef.value.contains(event.target)) {
    isAutocompleteVisible.value = false;
  }
};

onMounted(async () => {
  user.value.facebookUserName = "";
  user.value.password = "";
  user.value.confirmPassword = "";

  onboardingStore.setUsageType(props.isSchoolSignup ? "Edu" : "indiferente");
  registrationStep.value = 0;

  try {
    // GET /groups exige login e filtra por círculo: para quem ainda está se
    // cadastrando ele responde 401, e o cadastro abria com "Não foi possível
    // carregar as configurações de plano". O grupo "Todos" vem do endpoint
    // público dedicado, como no UserRegistrationForm (desktop).
    const response = await groupService.getGroupByName("Todos");
    trialPlanGroupId.value = response?.data?.groupId ?? null;
  } catch (error) {
    console.error("Erro ao buscar o grupo 'Todos':", error);
    showErrorToast("Não foi possível carregar as configurações de plano. Por favor, tente novamente.");
  }

  nextTick(() => fullNameInputRef.value?.focus());
  document.addEventListener("click", handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
  if (resendTimerId.value) { clearInterval(resendTimerId.value); resendTimerId.value = null; }
});

watch(registrationStep, (newStep) => {
  nextTick(() => {
    if (newStep === 0) emailInputRef.value?.focus();
    else if (newStep === 1) fullNameInputRef.value?.focus();
    else if (newStep === 2) (isClaimingAccount.value ? null : userNameInputRef.value)?.focus();
    else if (newStep === 4) {
      codeInputRefs.value[0]?.focus();
      startResendCooldown();
    }
  });
});
</script>

<template>
  <div class="page-container">
    <!-- Header fixo -->
    <div class="page-header">
      <span class="header-brand">
        <img src="/litterae_pena_fundo_transparente.png" alt="Logo Litterae" class="header-logo" />
        <span class="header-title">Litterae</span>
      </span>
      <button type="button" class="close-btn" @click="handleCloseRegistration" aria-label="Fechar cadastro">
        <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd"
            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
            clip-rule="evenodd" />
        </svg>
      </button>
    </div>

    <!-- Conteúdo -->
    <div class="page-body">
      <h3 class="page-title">Crie sua conta</h3>

      <!-- Stepper -->
      <div class="stepper">
        <div v-for="(item, i) in stepperItems" :key="item.label" class="stepper-item">
          <div :class="['stepper-dot', { active: item.active }]">{{ i + 1 }}</div>
          <div v-if="i < stepperItems.length - 1" :class="['stepper-line', { active: item.done }]"></div>
        </div>
      </div>
      <div class="stepper-label">
        <span>{{ currentStepLabel }}</span>
      </div>

      <form @submit.prevent="submitVerification">
        <!-- Campos iscas -->
        <input type="text" name="fake-username" autocomplete="username" class="sr-only" tabindex="-1" />
        <input type="password" name="fake-password" autocomplete="new-password" class="sr-only" tabindex="-1" />

        <!-- STEP 0: E-MAIL -->
        <div v-if="registrationStep === 0" class="step-content">
          <div class="form-group">
            <label for="m-email" class="form-label">E-mail</label>
            <input ref="emailInputRef" v-model="user.email" type="email" id="m-email"
              class="form-input" placeholder="seuemail@dominio.com" required
              @blur="checkRegistrationEmail" @keydown.enter.prevent="advanceStep" />
            <div v-if="isEmailTaken" class="taken-notice" role="alert">
              <p><strong>Este e-mail já tem uma conta no Litterae.</strong> Se for sua, entre com ela; se não lembra a senha, peça uma nova.</p>
              <div class="taken-actions">
                <button type="button" class="btn btn-primary" @click="emit('login-with', normalizedEmail())">Entrar</button>
                <button type="button" class="btn btn-secondary" @click="emit('forgot-password', normalizedEmail())">Esqueci minha senha</button>
              </div>
            </div>
            <div v-else-if="isClaimingAccount" class="claim-notice" role="status">
              <strong>Este e-mail já tem uma conta no Litterae</strong> como membro de uma
              instituição. Você só precisa escolher uma senha e confirmar o código que vamos
              enviar; depois disso a conta passa a ter também o seu espaço pessoal. Nome e data
              de nascimento continuam os já cadastrados.
            </div>
          </div>
        </div>

        <!-- STEP 1: PESSOAIS -->
        <div v-if="registrationStep === 1" class="step-content">
          <div v-if="isSchoolSignup" class="form-group">
            <label for="m-institutionName" class="form-label">Nome da instituição</label>
            <input v-model="institutionName" type="text" id="m-institutionName"
              class="form-input" placeholder="Ex.: Colégio Ananery" required />
          </div>
          <div class="form-group">
            <label for="m-fullName" class="form-label">{{
              isSchoolSignup ? "Nome completo do responsável pela conta" : "Nome Completo"
            }}</label>
            <input ref="fullNameInputRef" v-model="user.fullName" type="text" id="m-fullName"
              class="form-input" placeholder="Digite seu nome completo" required />
          </div>
          <div class="form-group">
            <label for="m-birthDate" class="form-label">Data de Nascimento</label>
            <input v-model="user.birthDate" type="date" id="m-birthDate"
              class="form-input" :max="maxBirthDate" required />
            <span v-if="user.birthDate && !isAdult" class="field-error" role="alert">
              Você precisa ter 18 anos ou mais para se cadastrar.
            </span>
          </div>
          <div class="form-group">
            <label for="m-country" class="form-label">País (opcional)</label>
            <input v-model="user.country" type="text" id="m-country"
              class="form-input" placeholder="Ex: Brasil" />
          </div>
          <div class="form-group">
            <label for="m-profession" class="form-label">Profissão (opcional)</label>
            <input v-model="user.profession" type="text" id="m-profession"
              class="form-input" placeholder="Ex: Desenvolvedor" />
          </div>
        </div>

        <!-- STEP 2: CONTA -->
        <div v-if="registrationStep === 2" class="step-content">
          <p class="email-summary">E-mail: <strong>{{ user.email.trim() }}</strong></p>
          <div v-if="isClaimingAccount" class="claim-notice" role="status">
            <strong>Este e-mail já tem uma conta no Litterae</strong> como membro de uma
            instituição. Escolha uma senha e confirme o código que vamos enviar; depois disso
            a conta passa a ter também o seu espaço pessoal. Nome e data de nascimento
            continuam os já cadastrados.
          </div>
          <div v-if="!isClaimingAccount" class="form-group">
            <label for="m-userName" class="form-label">Nome de Usuário</label>
            <input ref="userNameInputRef" v-model="user.userName" type="text" id="m-userName"
              class="form-input" placeholder="Escolha um nome de usuário" required />
          </div>
          <!-- Telefone -->
          <div v-if="!isClaimingAccount" class="form-group">
            <label class="form-label">Telefone</label>
            <div ref="ddiAutocompleteRef" class="ddi-autocomplete">
              <input type="text" class="form-input" v-model="ddiSearchTerm"
                @focus="handleDdiFocus" @blur="handleDdiBlur" placeholder="País (+código)" required />
              <ul v-if="isAutocompleteVisible" class="ddi-options">
                <li v-for="country in filteredCountries" :key="country.code" @mousedown.prevent="selectCountry(country)">
                  {{ country.name }} (+{{ country.code }})
                </li>
                <li v-if="!filteredCountries.length" class="ddi-no-results">Nenhum país encontrado.</li>
              </ul>
            </div>
            <div class="phone-row">
              <input v-model="phoneParts.ddd" @input="onDddInput" type="tel"
                class="form-input ddd-input" placeholder="DDD" maxlength="2" required />
              <input v-model="phoneParts.number" @input="onNumberInput" @blur="handlePhoneBlur"
                type="tel" class="form-input phone-input" placeholder="Número" maxlength="9" required />
            </div>
          </div>

          <!-- Senhas -->
          <div class="form-group">
            <label for="m-password" class="form-label">Senha</label>
            <div class="password-wrapper">
              <input v-model="user.password" :type="showPassword ? 'text' : 'password'"
                id="m-password" class="form-input" :class="{ 'input-error': passwordsMismatch }"
                placeholder="" required />
              <button type="button" @click="showPassword = !showPassword" class="password-toggle-btn">
                <svg v-if="!showPassword" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.542-7 1.274-4.057 5.064-7 9.542-7 1.422 0 2.774.376 3.982 1.025M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.938 17.938l-12-12" />
                </svg>
                <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </button>
            </div>
          </div>
          <div class="form-group">
            <label for="m-confirmPassword" class="form-label">
              Confirme sua Senha
              <span v-if="passwordsMismatch" class="label-inline-error" role="alert"> • Não coincidem</span>
            </label>
            <div class="password-wrapper">
              <input v-model="user.confirmPassword" :type="showConfirmPassword ? 'text' : 'password'"
                id="m-confirmPassword" class="form-input" :class="{ 'input-error': passwordsMismatch }"
                placeholder="" required />
              <button type="button" @click="showConfirmPassword = !showConfirmPassword" class="password-toggle-btn">
                <svg v-if="!showConfirmPassword" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.542-7 1.274-4.057 5.064-7 9.542-7 1.422 0 2.774.376 3.982 1.025M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.938 17.938l-12-12" />
                </svg>
                <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </button>
            </div>
          </div>
          <div class="password-hints" aria-live="polite">
            Use
            <span :class="{ met: passwordCriteriaMet.length }">8+ caracteres</span> com
            <span :class="{ met: passwordCriteriaMet.letter }">letras</span> e
            <span :class="{ met: passwordCriteriaMet.number }">números</span>.
          </div>
          <div class="terms-group">
            <input type="checkbox" id="m-terms" v-model="user.agreedToTerms" class="terms-checkbox" required />
            <span class="terms-label">
              Eu li e concordo com os
              <button type="button" class="terms-link-btn" @click="openLegalModal('terms')">Termos de Uso</button>
              e a
              <button type="button" class="terms-link-btn" @click="openLegalModal('privacy')">Política de Privacidade</button>.
            </span>
          </div>
        </div>

        <!-- STEP 3: REDES SOCIAIS -->
        <div v-if="registrationStep === 3" class="step-content">
          <div class="checkbox-row">
            <input type="checkbox" id="m-phoneIsWhatsapp" v-model="user.phoneIsWhatsapp" class="terms-checkbox" />
            <label for="m-phoneIsWhatsapp" class="terms-label">O telefone informado é meu WhatsApp</label>
          </div>
          <div class="checkbox-row">
            <input type="checkbox" id="m-isWhatsappPublic" v-model="user.isWhatsappPublic" class="terms-checkbox" />
            <label for="m-isWhatsappPublic" class="terms-label">Autorizo exibir meu WhatsApp no site</label>
          </div>
          <div class="form-group">
            <label for="m-telegram" class="form-label">Telegram (opcional)</label>
            <input v-model="user.telegramUserName" type="text" id="m-telegram"
              class="form-input" placeholder="@usuario ou telefone" />
          </div>
          <div class="form-group">
            <label for="m-instagram" class="form-label">Instagram (opcional)</label>
            <input v-model="user.instagramUserName" type="text" id="m-instagram"
              class="form-input" placeholder="@usuário" />
          </div>
          <div class="form-group">
            <label for="m-linkedin" class="form-label">LinkedIn (opcional)</label>
            <input v-model="user.linkedInUserName" type="text" id="m-linkedin"
              class="form-input" placeholder="url do perfil" />
          </div>
          <div class="form-group">
            <label for="m-facebook" class="form-label">Facebook (opcional)</label>
            <input v-model="user.facebookUserName" type="text" id="m-facebook"
              class="form-input" placeholder="url do perfil" />
          </div>
        </div>

        <!-- STEP 4: CONFIRMAÇÃO -->
        <div v-if="registrationStep === 4" class="step-content">
          <p class="confirmation-text">
            Enviamos um código de seis dígitos para <strong>{{ user.email }}</strong>.
            Insira o código abaixo para confirmar seu endereço de e-mail.
          </p>
          <div class="code-inputs-container">
            <input
              v-for="i in 6"
              :key="i"
              :ref="(el) => { if (el) codeInputRefs[i - 1] = el; }"
              @input="handleCodeInput($event, i - 1)"
              @keydown.delete="handleCodeBackspace($event, i - 1)"
              type="text"
              maxlength="1"
              class="code-input"
            />
          </div>
          <div class="info-box">
            Certifique-se de manter esta janela aberta enquanto verifica sua caixa de entrada.
          </div>

          <div class="resend-block">
            <button
              type="button"
              class="resend-btn"
              :class="{ 'resend-btn--ready': resendCooldown === 0 && !isResending }"
              :disabled="resendCooldown > 0 || isResending"
              @click="resendVerificationCode"
            >
              <svg v-if="resendCooldown > 0" class="resend-btn-icon" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" />
              </svg>
              <svg v-else class="resend-btn-icon" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M4 4h16v16H4z" /><path d="M4 4l8 8 8-8" />
              </svg>
              <span class="resend-btn-label">
                <template v-if="isResending">Enviando...</template>
                <template v-else-if="resendCooldown > 0">Aguarde {{ formatCooldown(resendCooldown) }} para novo código</template>
                <template v-else>Enviar novo código</template>
              </span>
              <span v-if="resendCooldown > 0" class="resend-btn-progress"
                :style="{ width: ((60 - resendCooldown) / 60) * 100 + '%' }"></span>
            </button>
          </div>
        </div>

        <!-- Footer de navegação -->
        <div class="page-footer">
          <button
            v-if="registrationStep < 4"
            type="button"
            @click="goBackStep"
            class="btn btn-secondary"
            :disabled="isLoading"
          >
            {{ registrationStep === 0 ? "Voltar ao início" : "Voltar" }}
          </button>

          <button
            v-if="registrationStep < 4"
            type="button"
            @click="advanceStep"
            class="btn btn-primary"
            :disabled="
              isLoading ||
              (registrationStep === 0 && (!isEmailStepValid || isEmailTaken)) ||
              (registrationStep === 1 && !isStep1Valid) ||
              (registrationStep === 2 && !isStep2Valid)
            "
          >
            {{ isLoading && (registrationStep === 3 || (registrationStep === 2 && isClaimingAccount)) ? "Enviando..." : "Avançar" }}
          </button>

          <button
            v-if="registrationStep === 4"
            type="submit"
            class="btn btn-success"
            :disabled="isLoading || !isCodeComplete"
          >
            {{ isLoading ? "Verificando..." : "Finalizar Cadastro" }}
          </button>
        </div>
      </form>
    </div>
  </div>

  <!-- LegalDocsModal -->
  <Teleport to="body">
    <LegalDocsModal v-if="showLegalModal" :defaultDoc="legalModalDefaultDoc" @close="closeLegalModal" />
    <WelcomeMobileModal v-if="showWelcomeMobileModal" @close="handleWelcomeMobileClose" />
  </Teleport>

  <!-- Toast -->
  <Teleport to="body">
    <transition name="toast-fade">
      <div v-if="showToast"
        class="fixed inset-0 pointer-events-none z-[9999] flex items-start justify-center pt-6 px-4">
        <div :class="['toast pointer-events-auto', toastType === 'error' ? 'toast-error' : 'toast-success']">
          <div class="toast-icon">
            <svg class="w-8 h-8" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="9" class="toast-icon-circle" />
              <path v-if="toastType === 'error'" d="M10 5v6M10 13.5v1.5"
                stroke="currentColor" stroke-width="2.2" stroke-linecap="round" />
              <path v-else d="M7.5 10.5 9.5 12.5 13 8.5"
                stroke="currentColor" stroke-width="2.2" stroke-linejoin="round" />
            </svg>
          </div>
          <div class="toast-content">
            <p class="toast-title">{{ toastType === "error" ? "Erro no Cadastro" : "Sucesso!" }}</p>
            <p class="toast-message">{{ toastMessage }}</p>
          </div>
          <button type="button" class="toast-close" @click="showToast = false" aria-label="Fechar">
            &times;
          </button>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style scoped>
/* ===== LAYOUT FULL-SCREEN ===== */
.page-container {
  position: fixed;
  inset: 0;
  z-index: 50;
  background: var(--login-gradient);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* ===== HEADER ===== */
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.85rem 1rem;
  background: rgba(255, 255, 255, 0.75);
  backdrop-filter: blur(8px);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  flex-shrink: 0;
}

.header-brand {
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.header-logo {
  width: 1.75rem;
  height: 1.75rem;
  object-fit: contain;
}

.header-title {
  font-size: 1.15rem;
  font-weight: 700;
  color: var(--text-dark);
}

.close-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-muted);
  padding: 0.4rem;
  border-radius: 8px;
  display: flex;
  align-items: center;
  transition: background-color 0.15s;
}

.close-btn:hover {
  background-color: rgba(0, 0, 0, 0.08);
}

/* ===== BODY ===== */
.page-body {
  flex: 1;
  overflow-y: auto;
  padding: 1.25rem 1rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.page-title {
  font-size: 1.2rem;
  font-weight: 700;
  color: var(--text-dark);
  text-align: center;
  margin: 0;
}

/* ===== STEPPER ===== */
.stepper {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
}

.stepper-item {
  display: flex;
  align-items: center;
}

.stepper-dot {
  width: 2rem;
  height: 2rem;
  border-radius: 9999px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  font-weight: 700;
  background: var(--neutral-200);
  color: var(--text-subtle);
  transition: all 0.2s;
  flex-shrink: 0;
}

.stepper-dot.active {
  background: var(--brand);
  color: var(--text-on-brand);
}

.stepper-line {
  width: 2.5rem;
  height: 2px;
  background: var(--neutral-200);
  transition: background 0.2s;
}

.stepper-line.active {
  background: var(--brand);
}

.stepper-label {
  text-align: center;
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--text-muted);
  margin-top: -0.25rem;
}

/* ===== FORM ===== */
.step-content {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

.form-input {
  min-height: 3rem;
}

.input-error {
  border-color: var(--danger) !important;
  box-shadow: var(--focus-ring-danger) !important;
}

.email-summary {
  margin: 0 0 0.75rem;
  font-size: 0.9rem;
  color: var(--text-light);
}
.taken-notice {
  margin-top: 0.5rem;
  padding: 0.6rem 0.75rem;
  border-radius: 8px;
  background: #fdecec;
  border: 1px solid #f3b4b4;
  color: #7a1f1f;
  font-size: 0.85rem;
  line-height: 1.4;
}
.taken-notice p {
  margin: 0 0 0.6rem;
}
.taken-actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}
.claim-notice {
  margin-top: 0.5rem;
  padding: 0.6rem 0.75rem;
  border-radius: 8px;
  background: #fff8e6;
  border: 1px solid #f5d98b;
  color: #6b4e00;
  font-size: 0.85rem;
  line-height: 1.4;
}
.field-error {
  font-size: 0.8rem;
  color: var(--danger-text);
}

/* DDI */
.ddi-autocomplete {
  position: relative;
}

.ddi-options {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: var(--surface);
  border: 1px solid var(--neutral-200);
  border-radius: 10px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  z-index: 20;
  list-style: none;
  padding: 0.4rem;
  margin-top: 0.25rem;
  max-height: 180px;
  overflow-y: auto;
}

.ddi-options li {
  padding: 0.55rem 0.75rem;
  font-size: 0.9rem;
  cursor: pointer;
  border-radius: 6px;
}

.ddi-options li:hover {
  background-color: var(--surface-2);
}

.ddi-no-results {
  text-align: center;
  color: var(--text-muted);
  font-style: italic;
  cursor: default;
}

/* Telefone */
.phone-row {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.4rem;
}

.ddd-input {
  flex: 0 0 70px;
}

.phone-input {
  flex: 1;
}

/* Senha */
.password-wrapper {
  position: relative;
}

.password-wrapper .form-input {
  padding-right: 3rem;
}

.password-toggle-btn {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-muted);
  padding: 0.25rem;
}

.password-hints {
  font-size: 0.82rem;
  color: var(--text-muted);
}

.password-hints .met {
  color: var(--success);
  text-decoration: line-through;
}

.label-inline-error {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--danger-text);
}

/* Termos */
.terms-group {
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  margin-top: 0.25rem;
}

.checkbox-row {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}

.terms-checkbox {
  width: 1.1rem;
  height: 1.1rem;
  flex-shrink: 0;
  margin-top: 0.15rem;
  cursor: pointer;
}

.terms-label {
  font-size: 0.88rem;
  color: var(--text-dark);
  line-height: 1.4;
}

.terms-link-btn {
  background: none;
  border: none;
  padding: 0;
  font-size: inherit;
  font-family: inherit;
  color: #6d28d9;
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.terms-link-btn:hover {
  color: var(--text-on-brand-soft);
}

/* Redes sociais */
/* (usa form-group / form-input padrão) */

/* ===== STEP 4 — CÓDIGO ===== */
.confirmation-text {
  font-size: 0.9rem;
  color: var(--text-muted);
  line-height: 1.55;
  text-align: center;
}

.code-inputs-container {
  display: flex;
  gap: 0.5rem;
  justify-content: center;
}

.code-input {
  width: 3rem;
  height: 3.25rem;
  text-align: center;
  font-size: 1.4rem;
  font-weight: 600;
  border-radius: 10px;
  border: 1px solid var(--neutral-200);
  background: var(--surface);
  -webkit-appearance: none;
}

.code-input:focus {
  outline: none;
  border-color: var(--focus-border);
  box-shadow: var(--focus-ring);
}

.info-box {
  background: var(--surface-2);
  border-radius: 8px;
  padding: 0.75rem;
  font-size: 0.82rem;
  color: var(--text-muted);
  text-align: center;
}

/* Reenvio */
.resend-block {
  display: flex;
  justify-content: center;
}

.resend-btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-family: inherit;
  font-weight: 500;
  border: 1px solid var(--neutral-200);
  border-radius: 8px;
  background: var(--surface-2);
  color: var(--text-muted);
  cursor: not-allowed;
  overflow: hidden;
  transition: background 0.2s, color 0.2s, border-color 0.2s;
}

.resend-btn-icon {
  width: 1rem;
  height: 1rem;
  flex-shrink: 0;
}

.resend-btn-label { position: relative; z-index: 1; }

.resend-btn-progress {
  position: absolute;
  left: 0;
  bottom: 0;
  height: 3px;
  background: var(--brand);
  transition: width 1s linear;
}

.resend-btn--ready {
  background: var(--surface);
  color: var(--text-on-brand);
  border-color: var(--brand);
  cursor: pointer;
  animation: resendPulse 1.8s ease-in-out infinite;
}

.resend-btn--ready:hover {
  background: var(--brand);
  color: var(--text-on-brand);
  animation: none;
}

.resend-btn:disabled { opacity: 1; }

@keyframes resendPulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(155, 132, 255, 0.45); }
  50%       { box-shadow: 0 0 0 6px rgba(155, 132, 255, 0); }
}

/* ===== FOOTER DE NAVEGAÇÃO ===== */
.page-footer {
  display: flex;
  gap: 0.65rem;
  padding: 0.75rem 0 0.5rem;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  margin-top: auto;
}

.btn {
  flex: 1;
}

.toast-title {
  margin-bottom: 0.2rem;
}

/* Acessibilidade */
.sr-only {
  position: absolute;
  left: -9999px;
  opacity: 0;
  height: 0;
  width: 0;
  border: 0;
  padding: 0;
  margin: 0;
}
</style>
